/**
 * Admin Payouts Hook
 * Fetch paginated creator payouts with filters and retry capability
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface AdminPayout {
  id: string;
  creatorId: string;
  periodLabel: string;
  totalFollowers: number;
  eligibleFollowers: number;
  grossRevenueCents: number;
  stripeFeesCents: number;
  platformShareCents: number;
  creatorShareCents: number;
  status: 'calculated' | 'transferred' | 'failed';
  stripeTransferId: string | null;
  transferredAt: string | null;
  transferError: string | null;
  calculatedAt: string;
  creatorName: string;
  creatorEmail: string;
}

export interface UseAdminPayoutsParams {
  periodLabel: string;
  status: string;
  page: number;
  limit: number;
}

export interface UseAdminPayoutsResult {
  payouts: AdminPayout[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  retryFailed: (periodLabel: string) => Promise<{ success: boolean; error?: string }>;
  retrying: boolean;
}

// =============================================================================
// Hook
// =============================================================================

export function useAdminPayouts(params: UseAdminPayoutsParams): UseAdminPayoutsResult {
  const { authFetch } = useAuth();
  const [payouts, setPayouts] = useState<AdminPayout[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        ...(params.periodLabel && { periodLabel: params.periodLabel }),
        ...(params.status && { status: params.status }),
      });

      const response = await authFetch(apiUrl(`/api/admin/payouts?${queryParams}`));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch payouts');
      }

      const data = await response.json();
      setPayouts(data.payouts);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch, params.page, params.limit, params.periodLabel, params.status]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const retryFailed = useCallback(
    async (periodLabel: string): Promise<{ success: boolean; error?: string }> => {
      setRetrying(true);
      try {
        const response = await authFetch(apiUrl('/api/admin/payouts/retry'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ periodLabel }),
        });

        if (!response.ok) {
          const data = await response.json();
          return { success: false, error: data.error || 'Retry failed' };
        }

        // Refresh list after retry
        await fetchPayouts();
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
      } finally {
        setRetrying(false);
      }
    },
    [authFetch, fetchPayouts],
  );

  return {
    payouts,
    total,
    pages,
    loading,
    error,
    refetch: fetchPayouts,
    retryFailed,
    retrying,
  };
}
